import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { dashboardApi } from '@/lib/api/dashboard'
import { modelsApi } from '@/lib/api/models'
import { scansApi, buildTriggerScanRequest } from '@/lib/api/scans'
import { settingsApi } from '@/lib/api/settings'
import type { DashboardSummary } from '@/lib/api/dashboard'
import { Button } from '@/components/ui/button'

function formatScanStatus(status?: string) {
  if (!status) return 'No scans yet'
  if (status === 'completed') return 'Completed'
  if (status === 'running') return 'Running'
  if (status === 'failed') return 'Failed'
  return status
}

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [scanMessage, setScanMessage] = useState<string | null>(null)

  const loadDashboard = useCallback(() => {
    dashboardApi
      .getDashboard()
      .then((data) => {
        setSummary(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const handleRunScan = async () => {
    if (!summary?.openRouterConnected) return

    setScanning(true)
    setScanMessage(null)

    try {
      const [categories, scanSettings] = await Promise.all([
        modelsApi.listCategories(),
        settingsApi.getScanSettings(),
      ])

      const enabledCategoryIds = categories.map((category) => category.id)

      if (enabledCategoryIds.length === 0) {
        setScanMessage('No enabled categories to scan.')
        return
      }

      const result = await scansApi.triggerScan(
        buildTriggerScanRequest(enabledCategoryIds, scanSettings),
      )
      setScanMessage(`Scan started (${result.status}).`)
      loadDashboard()
    } catch {
      setScanMessage('Failed to start scan.')
    } finally {
      setScanning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading dashboard...
      </div>
    )
  }

  if (!summary) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Failed to load dashboard.
      </div>
    )
  }

  const lastScanLabel = summary.lastScanAt
    ? `${new Date(summary.lastScanAt).toLocaleString()} · ${formatScanStatus(summary.lastScanStatus)}`
    : 'No scans yet'

  return (
    <div className="flex h-full flex-col" data-testid="dashboard-page">
      <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Model catalog overview</p>
        </div>
        <Button
          size="sm"
          className="min-h-11 w-full sm:w-auto"
          disabled={!summary.openRouterConnected || scanning || summary.lastScanStatus === 'running'}
          onClick={() => void handleRunScan()}
          data-testid="dashboard-run-scan"
        >
          {scanning ? 'Starting scan...' : 'Run Scan Now'}
        </Button>
      </div>

      {!summary.openRouterConnected ? (
        <div
          className="border-b border-border bg-muted/40 px-4 py-3 text-sm"
          data-testid="dashboard-connect-banner"
        >
          Connect your OpenRouter API key in{' '}
          <Link to="/settings" className="font-medium text-primary underline-offset-4 hover:underline">
            Settings
          </Link>{' '}
          to run scans.
        </div>
      ) : null}

      {scanMessage ? (
        <p className="px-4 py-2 text-sm text-muted-foreground" data-testid="dashboard-scan-message">
          {scanMessage}
        </p>
      ) : null}

      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Models" value={summary.totalModels} testId="dashboard-stat-total-models" />
        <StatCard label="Free" value={summary.freeModels} testId="dashboard-stat-free-models" />
        <StatCard label="Active" value={summary.activeModels} testId="dashboard-stat-active-models" />
        <StatCard
          label="Last Scan"
          value={lastScanLabel}
          testId="dashboard-stat-last-scan"
          compact
        />
      </div>

      <div className="px-4 pb-2">
        <Link
          to="/scans"
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          data-testid="dashboard-scan-history-link"
        >
          View scan history
        </Link>
      </div>

      <div className="grid flex-1 gap-4 overflow-auto px-4 pb-4 lg:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Top Models by Category</h2>
          {summary.topModelsByCategory.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground" data-testid="dashboard-empty-models">
              No models yet. Run your first scan.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {summary.topModelsByCategory.map((category) => (
                <div key={category.categoryName}>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {category.categoryName}
                  </p>
                  <ul className="mt-1 space-y-1">
                    {category.models.map((model, index) => (
                      <li
                        key={model.modelId}
                        className="flex items-center justify-between text-sm"
                        data-testid={`dashboard-top-model-${model.modelId}`}
                      >
                        <span className="truncate">
                          {index + 1}. {model.displayName}
                        </span>
                        <span className="text-muted-foreground">{model.categoryScore.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Warnings</h2>
          {summary.warnings.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No warnings.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {summary.warnings.map((warning, index) => (
                <li
                  key={`${warning.categoryName}-${index}`}
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100"
                  data-testid="dashboard-warning"
                >
                  {warning.categoryName}: {warning.message}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  testId,
  compact = false,
}: {
  label: string
  value: number | string
  testId: string
  compact?: boolean
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={compact ? 'mt-2 text-sm font-medium' : 'mt-2 text-3xl font-bold'}>{value}</p>
    </div>
  )
}
